import { useCallback, useMemo, useRef, useState } from "react";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	ColGroupDef,
	GridApi,
	ValueFormatterParams,
} from "ag-grid-community";
import { ErrorModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import moment, { type Moment } from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	HEADER_PAD_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import {
	useBilletsProcessData,
	useBilletsProcessPickers,
} from "../hooks/useBilletsProcessData";
import {
	BILLET_PROCESS_VARIABLE_NAMES,
	STAND_COLUMNS,
	getStandTooltip,
	type BilletProcessRow,
	type BilletProcessVariableName,
	type StandColumnName,
} from "../models/BilletsProcessData";
import { BilletsProcessDataSummary } from "./BilletsProcessDataSummary";
import { BilletsProcessDataToolbar } from "./BilletsProcessDataToolbar";

const CENTER_HEADER = "ag-cell-center-header";
const HEADER_CLASS = [CENTER_HEADER, HEADER_PAD_CLASS];
// Legacy centres every column; tabular figures keep the digits of a column aligned.
const CELL_CLASS = ONE_LINE_CELL_CLASS;
const NUMBER_STYLE = { fontVariantNumeric: "tabular-nums" } as const;
const STAND_WIDTH = 56;

// SP CASTs these BIT columns to varchar via dynamic SQL → arrive as "0"/"1".
const BIT_VARIABLES: ReadonlySet<BilletProcessVariableName> = new Set([
	"TensionControl",
	"Selected",
	"Working",
	"LooperControlSelected",
]);

const TEXT_VARIABLES: ReadonlySet<BilletProcessVariableName> = new Set([
	"RollSet",
	"BottomRoll",
	"TopRoll",
	"PassName",
]);

const formatStandValue = (
	raw: unknown,
	variable: BilletProcessVariableName,
): string => {
	if (raw === null || raw === undefined || raw === "") return "";
	const s = String(raw);
	if (BIT_VARIABLES.has(variable)) return s === "1" ? "✓" : "";
	if (TEXT_VARIABLES.has(variable)) return s;
	const n = Number(s);
	if (!Number.isFinite(n)) return s;
	return String(Number(n.toFixed(3)));
};

const formatNumber = (value: unknown, decimals: number): string =>
	value == null || value === ""
		? ""
		: Number(value).toLocaleString("en-US", {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			});

const buildStandColumns = (
	variable: BilletProcessVariableName,
): ColDef<BilletProcessRow>[] =>
	STAND_COLUMNS.map<ColDef<BilletProcessRow>>((standName) => ({
		field: standName as StandColumnName,
		headerName: standName,
		headerTooltip: getStandTooltip(standName),
		width: STAND_WIDTH,
		cellClass: CELL_CLASS,
		cellStyle: NUMBER_STYLE,
		headerClass: HEADER_CLASS,
		valueFormatter: (p: ValueFormatterParams<BilletProcessRow>) =>
			formatStandValue(p.value, variable),
	}));

// Wraps a single ColDef in an empty group so its column header sits at the
// same vertical level as the stand columns nested under the "<variable>" group.
const wrapEmptyGroup = (
	col: ColDef<BilletProcessRow>,
): ColGroupDef<BilletProcessRow> => ({
	headerName: "",
	children: [col],
});

const buildColumnDefs = (
	variable: BilletProcessVariableName,
): (ColDef<BilletProcessRow> | ColGroupDef<BilletProcessRow>)[] => [
	wrapEmptyGroup({
		field: "StartString",
		headerName: "Start",
		width: 150,
		sortable: true,
		sort: "desc",
		cellClass: CELL_CLASS,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) =>
			p.data?.Start
				? dxtToLocalServerTime(p.data.Start, "yyyy-MM-dd HH:mm:ss")
				: "",
	}),
	wrapEmptyGroup({
		field: "ProductName",
		headerName: "Product",
		flex: 1,
		minWidth: 180,
		sortable: true,
		cellClass: CELL_CLASS,
		headerClass: HEADER_CLASS,
		tooltipField: "ProductName",
	}),
	wrapEmptyGroup({
		field: "LineSpeed",
		headerName: "Line Speed",
		width: 82,
		cellClass: CELL_CLASS,
		cellStyle: NUMBER_STYLE,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 0),
	}),
	wrapEmptyGroup({
		field: "LineSpeed2",
		headerName: "Line Speed 2",
		width: 92,
		cellClass: CELL_CLASS,
		cellStyle: NUMBER_STYLE,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 0),
	}),
	wrapEmptyGroup({
		field: "Gap",
		headerName: "Gap S1",
		width: 70,
		cellClass: CELL_CLASS,
		cellStyle: NUMBER_STYLE,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 2),
	}),
	wrapEmptyGroup({
		field: "Gap17",
		headerName: "Gap S17",
		width: 74,
		cellClass: CELL_CLASS,
		cellStyle: NUMBER_STYLE,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 2),
	}),
	wrapEmptyGroup({
		field: "CrewId",
		headerName: "Shift",
		width: 52,
		sortable: true,
		cellClass: CELL_CLASS,
		headerClass: HEADER_CLASS,
	}),
	wrapEmptyGroup({
		field: "EndString",
		headerName: "End",
		width: 150,
		hide: true,
		sortable: true,
		cellClass: CELL_CLASS,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) =>
			p.data?.End
				? dxtToLocalServerTime(p.data.End, "yyyy-MM-dd HH:mm:ss")
				: "",
	}),
	wrapEmptyGroup({
		field: "Length",
		headerName: "Length",
		width: 70,
		hide: true,
		sortable: true,
		cellClass: CELL_CLASS,
		cellStyle: NUMBER_STYLE,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 2),
	}),
	wrapEmptyGroup({
		field: "BilletWeight",
		headerName: "Billet Weight",
		width: 96,
		hide: true,
		sortable: true,
		cellClass: CELL_CLASS,
		cellStyle: NUMBER_STYLE,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 3),
	}),
	wrapEmptyGroup({
		field: "BilletSize",
		headerName: "Billet Size",
		width: 84,
		hide: true,
		cellClass: CELL_CLASS,
		headerClass: HEADER_CLASS,
	}),
	{
		headerName: variable,
		headerClass: HEADER_CLASS,
		marryChildren: true,
		children: buildStandColumns(variable),
	},
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	// The dropdown shows "All" (null) while the procedure always gets a real variable,
	// so null stays out of the URL rather than being written as SpeedMax.
	variableName: urlParam.oneOf(BILLET_PROCESS_VARIABLE_NAMES),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	crewId: urlParam.string(),
};

export const BilletsProcessDataReport = () => {
	const [
		{ start, end, variableName, productTypeId, productSizeId, crewId },
		setFilters,
	] = useUrlState(URL_SCHEMA);
	// UI shows "All" (state=null) but the SP always gets a real variable —
	// matches legacy IHBox where the empty dropdown still loads data with
	// SpeedMax (the SP's defensive default kicks in via WCF stripping the
	// blank param). Keeps the page auto-loading on mount + the dropdown
	// labeled "All" until the operator picks.
	const effectiveVariableName: BilletProcessVariableName =
		variableName ?? "SpeedMax";
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<BilletProcessRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const pickersQuery = useBilletsProcessPickers({
		start: startStr,
		end: endStr,
	});

	const dataQuery = useBilletsProcessData({
		start: startStr,
		end: endStr,
		variableName: effectiveVariableName,
		crewId,
		productSizeId,
		productTypeId,
	});

	const columnDefs = useMemo(
		() => buildColumnDefs(effectiveVariableName),
		[effectiveVariableName],
	);

	const rows = dataQuery.data ?? [];

	const handleRefresh = useCallback(() => {
		pickersQuery.refetch();
		dataQuery.refetch();
	}, [pickersQuery, dataQuery]);

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `Billets_Process_Data_${startStr}_${endStr}.xlsx`,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				const data = params.node?.data as BilletProcessRow | undefined;
				if (colId === "StartString") {
					return data?.Start
						? dxtToLocalServerTime(data.Start, "yyyy-MM-dd HH:mm:ss")
						: "";
				}
				if (colId === "EndString") {
					return data?.End
						? dxtToLocalServerTime(data.End, "yyyy-MM-dd HH:mm:ss")
						: "";
				}
				if ((STAND_COLUMNS as readonly string[]).includes(colId)) {
					return formatStandValue(params.value, effectiveVariableName);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr, effectiveVariableName]);

	const isInitialLoading = dataQuery.isLoading || pickersQuery.isLoading;

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 64px)",
				gap: 1,
				p: 1,
				pb: 2,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Billet Process Data
			</Typography>
			<OneLineCellStyles />
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<BilletsProcessDataToolbar
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					variableName={variableName}
					onVariableNameChange={(name) => setFilters({ variableName: name })}
					productTypeId={productTypeId}
					productSizeId={productSizeId}
					crewId={crewId}
					onProductTypeChange={(id) => {
						const stillValid =
							productSizeId == null ||
							(pickersQuery.data?.productSizes ?? []).some(
								(s) =>
									s.Id === productSizeId &&
									(id == null || s.ProductTypeId === id),
							);
						setFilters({
							productTypeId: id,
							productSizeId: stillValid ? productSizeId : null,
						});
					}}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					onCrewChange={(id) => setFilters({ crewId: id })}
					variableNames={pickersQuery.data?.variableNames ?? []}
					productTypes={pickersQuery.data?.productTypes ?? []}
					productSizes={pickersQuery.data?.productSizes ?? []}
					crews={pickersQuery.data?.crews ?? []}
					onRefresh={handleRefresh}
					onExportExcel={handleExportToExcel}
					isLoading={dataQuery.isFetching || pickersQuery.isFetching}
				/>
			</Paper>

			<BilletsProcessDataSummary total={rows.length} />

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
				}}
			>
				{isInitialLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<BilletProcessRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{
							resizable: true,
							wrapText: false,
							autoHeight: false,
						}}
						enableBrowserTooltips
						suppressContextMenu
						headerHeight={32}
						groupHeaderHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
